import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import notificationsService from '../../services/notificationsService';
import NotificationItem from '../../pages/writer-dashboard/components/NotificationItem';


const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAvatarDropdown, setShowAvatarDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userProfile, signOut } = useAuth();
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  // Fetch unread notification count
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    const fetchUnread = async () => {
      try {
        const { data } = await notificationsService.getUnreadCount();
        setUnreadCount(data.count || 0);
      } catch {
        // Silently fail
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Hide header on article reading page and discussion circle page
  const isArticlePage = location?.pathname?.startsWith('/article/');
  const isDiscussionPage = location?.pathname?.startsWith('/discussion/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowNotificationDropdown(false);
  }, [location?.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setShowAvatarDropdown(false);
      }
      if (notificationRef?.current && !notificationRef?.current?.contains(event?.target)) {
        setShowNotificationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = [
    { path: '/homepage', label: 'الرئيسية', labelEn: 'Home' },
    { path: '/about-dawaaer', label: 'عن دوائر', labelEn: 'About Dawaaer' },
    { path: '/about', label: 'من نحن؟', labelEn: 'About Us' },
    { path: '/policy', label: 'سياستنا', labelEn: 'Our Policy' }];


  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleCTAClick = () => {
    if (isAuthenticated) {
      navigate('/creator-studio');
    } else {
      navigate('/signup', { state: { directToForm: true } });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setShowAvatarDropdown(false);
    navigate('/homepage');
  };

  const handleToggleNotifications = async () => {
    const willOpen = !showNotificationDropdown;
    setShowNotificationDropdown(willOpen);
    if (willOpen) {
      setShowAvatarDropdown(false);
      setLoadingNotifications(true);
      try {
        const { data } = await notificationsService.getNotifications();
        setNotifications(Array.isArray(data) ? data : []);
      } catch {
        setNotifications([]);
      } finally {
        setLoadingNotifications(false);
      }
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silent
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  };

  // Don't render header on article page
  if (isArticlePage || isDiscussionPage) {
    return null;
  }

  return (
    <>
      <header className={`header ${isScrolled ? 'shadow-warm-lg' : ''}`}>
        <div className="header-container">
          <div className="header-content" style={{ paddingTop: '24px', paddingBottom: '24px' }}>
            <div className="header-logo-container">
              <div className="flex flex-col items-center gap-1 mb-[9px]">
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/homepage')}>
                  <div className="header-logo mt-[90px] mb-[-23px] ml-1">
                    <img
                      src="/assets/images/IMG_7338.png"
                      alt="Dawayer Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <span className="text-[48px] mb-[-21px] mt-[85px]" style={{ fontFamily: 'El Messiri', color: 'var(--color-primary)' }}>دوائر</span>
                </div>
                <p className="text-center max-w-md mt-0.5 text-base mb-[31px]" style={{ fontFamily: 'Amiri, serif', fontWeight: 400, color: 'var(--color-muted-foreground)', marginTop: '16px', marginBottom: '40px' }}>
                  نغرّد داخل السِرب؛ آملين أن تنبُتَ للجميع أجنحة
                </p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center gap-6 z-10 px-6">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`text-foreground hover:text-primary transition-colors duration-300 font-medium relative text-lg ${isActivePath(item?.path) ? 'text-primary' : ''}`}
                  style={{ fontFamily: 'Amiri, serif', fontWeight: 400 }}>
                  {item?.label}
                  {isActivePath(item?.path) && (
                    <span className="absolute bottom-0 right-0 w-full h-0.5 bg-primary" />
                  )}
                </button>
              ))}
            </nav>

            {/* Actions */}
            <div className="hidden lg:flex items-center gap-4 z-10">
              <Button
                variant="default"
                onClick={handleCTAClick}
                className="rounded-full px-8 py-3 shadow-warm text-base"
                style={{
                  fontFamily: 'Amiri, serif',
                  fontWeight: 400,
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  border: 'none'
                }}>
                {isAuthenticated ? 'اترك أثرًا' : 'اترك أثرًا'}
              </Button>

              {/* Admin Content Management Button - Only visible to admins */}
              {userProfile?.role === 'admin' && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/dashboard')}
                  className="rounded-full px-8 py-3 shadow-warm text-base"
                  style={{
                    fontFamily: 'Amiri, serif',
                    fontWeight: 400,
                    border: '2px solid var(--color-primary)',
                    color: 'var(--color-primary)',
                    backgroundColor: 'transparent'
                  }}>
                  إدارة المحتوى
                </Button>
              )}

              {/* Notification Bell (only when authenticated) */}
              {isAuthenticated && (
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={handleToggleNotifications}
                    className="relative p-2 rounded-full hover:bg-[var(--color-muted)] transition-colors"
                    aria-label="الإشعارات"
                  >
                    <Icon name="Bell" size={24} color="var(--color-foreground)" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotificationDropdown && (
                    <div className="absolute left-0 top-12 bg-white rounded-xl shadow-warm-lg z-50 w-80 md:w-96 max-h-[70vh] flex flex-col animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
                        <h3 className="text-base font-bold text-foreground" style={{ fontFamily: 'Amiri, serif' }}>
                          الإشعارات
                        </h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllAsRead}
                            className="text-xs text-[var(--color-primary)] hover:text-[var(--color-secondary)] transition-colors"
                            style={{ fontFamily: 'Amiri, serif' }}
                          >
                            تحديد الكل كمقروء
                          </button>
                        )}
                      </div>

                      <div className="overflow-y-auto flex-1">
                        {loadingNotifications ? (
                          <div className="p-4 space-y-3">
                            {[...Array(3)].map((_, i) => (
                              <div key={i} className="flex items-start gap-3 animate-pulse">
                                <div className="w-8 h-8 rounded-full bg-[var(--color-muted)] flex-shrink-0" />
                                <div className="flex-1 space-y-2">
                                  <div className="w-3/4 h-3 rounded bg-[var(--color-muted)]" />
                                  <div className="w-1/2 h-3 rounded bg-[var(--color-muted)]" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : notifications.length > 0 ? (
                          notifications.slice(0, 10).map((notification) => (
                            <NotificationItem
                              key={notification?.id}
                              notification={notification}
                              onMarkAsRead={handleMarkAsRead}
                              onNavigate={() => setShowNotificationDropdown(false)}
                            />
                          ))
                        ) : (
                          <div className="p-8 text-center">
                            <Icon name="Bell" size={32} className="mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Amiri, serif' }}>
                              لا توجد إشعارات
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-[var(--color-border)] px-4 py-3">
                        <button
                          onClick={() => {
                            navigate('/writer-dashboard?tab=notifications');
                            setShowNotificationDropdown(false);
                          }}
                          className="w-full text-center text-sm text-[var(--color-primary)] hover:text-[var(--color-secondary)] transition-colors"
                          style={{ fontFamily: 'Amiri, serif' }}
                        >
                          عرض جميع الإشعارات
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Avatar with Dropdown (only when authenticated) */}
              {isAuthenticated && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => { setShowAvatarDropdown(!showAvatarDropdown); setShowNotificationDropdown(false); }}
                    className="w-12 h-12 rounded-full overflow-hidden border-2 border-[var(--color-primary)] hover:border-[var(--color-accent)] transition-colors shadow-warm"
                  >
                    {userProfile?.avatar_url ? (
                      <img
                        src={userProfile?.avatar_url}
                        alt="صورة المستخدم"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-primary)]/5 flex items-center justify-center">
                        <Icon name="User" size={24} color="var(--color-primary)" />
                      </div>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {showAvatarDropdown && (
                    <div className="absolute left-0 top-14 bg-white border border-[var(--color-border)] rounded-xl shadow-warm-lg py-2 z-50 min-w-[200px] animate-in fade-in slide-in-from-top-2">
                      <button
                        onClick={() => {
                          navigate('/user-profile');
                          setShowAvatarDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-background)] transition-colors text-right"
                      >
                        <Icon name="User" size={18} color="var(--color-foreground)" />
                        <span className="text-sm" style={{ fontFamily: 'Amiri, serif' }}>ملفي الشخصي</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate('/writer-dashboard');
                          setShowAvatarDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-background)] transition-colors text-right"
                      >
                        <Icon name="LayoutGrid" size={18} color="var(--color-foreground)" />
                        <span className="text-sm" style={{ fontFamily: 'Amiri, serif' }}>لوحة الكاتب</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setShowAvatarDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-background)] transition-colors text-right"
                      >
                        <Icon name="Settings" size={18} color="var(--color-foreground)" />
                        <span className="text-sm" style={{ fontFamily: 'Amiri, serif' }}>الإعدادات</span>
                      </button>
                      <div className="border-t border-[var(--color-border)] my-2" />
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-background)] transition-colors text-right text-[var(--color-error)]"
                      >
                        <Icon name="LogOut" size={18} color="var(--color-error)" />
                        <span className="text-sm" style={{ fontFamily: 'Amiri, serif' }}>تسجيل الخروج</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <button
        onClick={toggleMobileMenu}
        className="mobile-menu-button"
        aria-label={isMobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}>
        <Icon
          name={isMobileMenuOpen ? 'X' : 'Menu'}
          size={24}
          color="var(--color-foreground)" />
      </button>
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : 'closed'}`}>
        <div className="mobile-menu-content">
          <nav className="mobile-menu-nav">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`mobile-menu-link ${isActivePath(item?.path) ? 'active' : ''}`}>
                {item?.label}
              </button>
            ))}
            {isAuthenticated && (
              <>
                <button
                  onClick={() => handleNavigation('/writer-dashboard')}
                  className="mobile-menu-link flex items-center gap-3"
                >
                  <Icon name="LayoutGrid" size={20} color="var(--color-foreground)" />
                  <span>لوحة الكاتب</span>
                </button>
                <button
                  onClick={() => handleNavigation('/settings')}
                  className="mobile-menu-link flex items-center gap-3"
                >
                  <Icon name="Settings" size={20} color="var(--color-foreground)" />
                  <span>الإعدادات</span>
                </button>
                <button
                  onClick={() => handleNavigation('/writer-dashboard?tab=notifications')}
                  className="mobile-menu-link flex items-center gap-3"
                >
                  <Icon name="Bell" size={20} color="var(--color-foreground)" />
                  <span>الإشعارات</span>
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </>
            )}
          </nav>

          <div className="mobile-menu-cta">
            <Button
              variant="default"
              fullWidth
              onClick={() => handleNavigation('/signup')}
              className="rounded-full shadow-warm"
              style={{
                fontFamily: 'Amiri, serif',
                fontWeight: 400,
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none'
              }}>
              اخلق دائرتك
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
